import { useRef, useState } from "react";

/**
 * Custom hook to manage asynchronous loading of child nodes.
 */
export const useAsyncLoadChildren = () => {
  const asyncNodeList = useRef(new Map<number, Promise<void>>());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  /**
   * Updates the loading queue with a new promise.
   */
  const updateLoadingQueue = (index: number, promise: Promise<void>) => {
    const queue = asyncNodeList.current.set(index, promise);
    promise
      .then(() => {
        asyncNodeList.current.delete(index);
        if (asyncNodeList.current.size === 0) {
          setLoading(false);
        }
      })
      .catch(() => {
        setError(true);
      });
    setLoading(true);
    return queue;
  };

  /**
   * Memoizes the loading state for a given node.
   */
  const memoizeLoading = (index: number, promiseGetter: (index: number) => Promise<void>) =>
    asyncNodeList.current.get(index) ?? updateLoadingQueue(index, promiseGetter(index)).get(index) ?? [];

  return { memoizeLoading, loading, error };
};
